import React, { useState } from 'react';
import { Box, Grid } from '@mui/material';
import { useField } from 'react-final-form';

import { Radios, TextField } from '@occc-app/design-system';
import { Typography, Button, BarcodeIcon } from '@occc/ui-design-system';
import { Product_Group_Key, ProductGroupInput } from '@occc/graphql';

import { ValueBadgeField } from './ValueBadgeField';
import { generateUpcVariants } from '../generateUpcVariants';
import { PRODUCT_GROUP_FIELD_NAME, productGroupKeysMap, TAR_SWITCH_FIELD_NAME } from '../constants';

const fieldDisabled = (name: string, values: ProductGroupInput) => {
  if (name === 'key') {
    return Boolean(Array.isArray(values.values) && values.values.length);
  }
  return false;
};

const pgValuesValueType = (key: Product_Group_Key) =>
  `one ${productGroupKeysMap[key].toLowerCase()} value at a time, or paste a comma separated list`;

const radioButtonLabel = (values: ProductGroupInput['values']) => {
  const hasItems = Array.isArray(values) && values.length;
  return `Identify line items by${
    hasItems
      ? // tslint:disable-next-line
        ' (cannot modify once Applicable values have been provided, please remove Applicable values, or refresh the page to start over)'
      : ''
  }`;
};

type GenerateCodesAndAddProps = {
  handleAddValue: () => void;
};

const GenerateCodesAndAdd = ({ handleAddValue }: GenerateCodesAndAddProps) => (
  <Button variant='outlined' color='primary' onClick={handleAddValue} startIcon={<BarcodeIcon />}>
    Generate codes and add
  </Button>
);

const radioButtonData = Object.keys(productGroupKeysMap).map(
  (value: keyof typeof productGroupKeysMap) => ({
    label: productGroupKeysMap[value],
    value,
  }),
);

interface IProductGroupFormContentProps {
  values: ProductGroupInput;
  readOnly: boolean;
  title: string;
  disabledGroupTypeChange?: boolean;
}

export const ProductGroupFormContent: React.FC<IProductGroupFormContentProps> = ({
  values,
  readOnly,
  title,
  disabledGroupTypeChange,
}) => {
  const [previousProductGroupKey, setPreviousProductGroupKey] = useState<Product_Group_Key>(
    values.key,
  );
  const productGroupKeyField = useField(PRODUCT_GROUP_FIELD_NAME);
  const tarSwitchField = useField(TAR_SWITCH_FIELD_NAME);

  if (previousProductGroupKey !== productGroupKeyField.input.value) {
    tarSwitchField.input.onChange(
      productGroupKeyField.input.value === Product_Group_Key.FuelGradeId,
    );
    setPreviousProductGroupKey(productGroupKeyField.input.value);
  }

  const productGroupKey = React.useMemo(() => {
    const isGroupTypeFieldDisabled =
      fieldDisabled(PRODUCT_GROUP_FIELD_NAME, values) || disabledGroupTypeChange;
    return readOnly ? null : (
      <Box mt={2} mb={3}>
        <Radios
          name={PRODUCT_GROUP_FIELD_NAME}
          label={radioButtonLabel(values.values)}
          disabled={isGroupTypeFieldDisabled}
          data={radioButtonData}
          radioGroupProps={{
            sx: { flexDirection: 'row' },
          }}
        />
      </Box>
    );
  }, [readOnly, values, disabledGroupTypeChange]);

  return (
    <>
      <Typography variant='h6' gutterBottom>
        {title}
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <TextField name='name' label='Name' InputProps={{ readOnly }} margin='normal' />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            name='description'
            label='Description'
            multiline
            InputProps={{ readOnly }}
            margin='normal'
            fieldProps={{ parse: (value) => value }}
          />
        </Grid>
      </Grid>
      {productGroupKey}
      <ValueBadgeField
        name='values'
        addedValuesCopy={`Configured ${productGroupKeysMap[values.key].toLowerCase()}s`}
        readonly={readOnly}
        removeAllAddedValuesCopy='Remove All Values'
        valueType={pgValuesValueType(values.key)}
        valuesPreprocessor={values.key === Product_Group_Key.Upc ? generateUpcVariants : undefined}
        additionalButton={values.key === Product_Group_Key.Upc ? GenerateCodesAndAdd : undefined}
      />
    </>
  );
};
